import { useNavigate } from "react-router-dom";
import Aside from "../../components/Aside";
import CustomDataTable from "../../components/CustomDataTable";
import Footer from "../../components/Footer";
import Header from "../../components/Header";
import { BulkSenderService } from "../../services/BulkSenderService";
import { useEffect, useState } from "react";
import { useCurrentAccount } from "../../hooks/useCurrentAccount";
import PieChart from "../../components/PieChart";
import Button from "../../components/form-controls/Button";
import { AlertService } from "../../services/Alert";
import { DateUtil } from "../../utils/date";
import { useTranslation } from "react-i18next";
import useRequest from "../../hooks/useRequest";
import { ChannelUtil } from "../../utils/channel";
function BulkSendersPage() {
    const { t } = useTranslation();
    const { account } = useCurrentAccount();
    const { call, isRequesting } = useRequest();
    const navigate = useNavigate();

    const [data, setData] = useState([])
    const [filterRangeDates, setFilterRangeDates] = useState({ start: null, end: null })

    const columns = [
        {
            name: t("bulk-senders.name"),
            sortable: true, center: true,
            cell: (row: any) => {
                return (
                    <h4 className="mb-0 text-nowrap">
                        {row.info.base_name}
                    </h4>
                )
            }
        },
        {
            name: t('bulk-senders.app'),
            sortable: false,
            center: true,
            cell: row => {
                return (
                    <div>
                        {(() => {
                            switch (ChannelUtil.format(row.info.app)) {
                                case 'WhatsApp Business':
                                    return <div><span className="badge bg-soft-primary text-primary"> <i className="bi bi-whatsapp"></i> {t("platform.channels.links-page.wa-business")} </span></div>;
                                case 'RCS':
                                    return <div><span className="badge badge-soft-rcs"> <i className="bi bi-chat-left-text"></i> {t("platform.channels.links-page.rcs")} </span></div>;
                                case 'SMS':
                                    return <div><span className="badge bg-soft-info text-info"> <i className="bi bi-chat-text"></i> {t("platform.channels.links-page.sms")} </span></div>;
                                default:
                                    return <div><span className="badge bg-soft-danger text-danger">{t("platform.channels.links-page.badge-alert")} </span></div>;
                            }
                        })()}
                    </div>
                )
            },
        },

        {
            name: t("bulk-senders.statistics"),
            sortable: false,
            center: true,
            cell: (row: any) => {
                let resume = {
                    total_read: {
                        label: t("bulk-senders.read"),
                        total: 0,
                        color: '#FAA916',
                    },
                    total_delivered: {
                        label: t("bulk-senders.delivered"),
                        total: 0,
                        color: '#579743',
                    },
                    total_sent: {
                        label: t("bulk-senders.sent"),
                        total: 0,
                        color: '#0070BC',
                    },
                    total_no_sent: {
                        label: t("bulk-senders.no-sent"),
                        total: 0,
                        color: '#575A5C',
                    },
                    total_failed: {
                        label: t("bulk-senders.failed"),
                        total: 0,
                        color: '#CE4532',
                    },
                    total_convertion: {
                        label: t("bulk-senders.convertion"),
                        total: 0,
                        color: '#13A1AB',
                    }
                };

                const keys = Object.keys(row.chart);
                keys.forEach(k => {
                    if (resume[k]) {
                        resume[k].total = row.chart[k];
                    }
                });
                return (
                    <div style={{ marginLeft: '6rem' }}>
                        <PieChart
                            datasetName='Resumen'
                            dataset={{
                                labels: [resume.total_read.label, resume.total_delivered.label, resume.total_sent.label, resume.total_no_sent.label, resume.total_failed.label, resume.total_convertion.label],
                                data: [resume.total_read.total, resume.total_delivered.total, resume.total_sent.total, resume.total_no_sent.total, resume.total_failed.total, resume.total_convertion.total],
                                backgroundColor: [resume.total_read.color, resume.total_delivered.color, resume.total_sent.color, resume.total_no_sent.color, resume.total_failed.color, resume.total_convertion.color],
                            }}
                        />
                    </div>
                );
            }
        },
        {
            name: t("bulk-senders.data-sent"),
            sortable: false,
            center: false,
            cell: (row: any) => {
                const getStatusDetails = (status) => {
                    switch (status) {
                        case 'ACTIVA':
                            return {
                                className: 'badge bg-soft-primary text-primary fs-6',
                                icon: <i className="bi bi-check-circle-fill fs-5"></i>,
                                statusText: t("bulk-senders.data-status-active")
                            };
                        case 'LIMPIANDO':
                            return {
                                className: 'badge bg-soft-info text-info fs-6',
                                icon: <i className="bi bi-brush fs-5"></i>,
                                statusText: t("bulk-senders.data-status-clean")
                            };
                        case 'ENVIANDO':
                            return {
                                className: 'badge bg-soft-success text-success fs-6',
                                icon: <i className="bi bi-send fs-5"></i>,
                                statusText: t("bulk-senders.data-status-sending")
                            };
                        case 'DESACTIVA':
                            return {
                                className: 'badge bg-soft-danger text-danger fs-6',
                                icon: <i className="bi bi-dash-circle-fill fs-5"></i>,
                                statusText: t("bulk-senders.data-status-inactive")
                            };
                        default:
                            return {
                                className: 'badge bg-soft-warning text-warning fs-6',
                                icon: <i className="bi bi-bookmark-fill fs-5"></i>,
                                statusText: t("bulk-senders.data-no-info")
                            };
                    }
                };

                const statusDetails = getStatusDetails(row.status.process_status);

                return (
                    <>
                        <p className="mb-0 text-nowrap">
                            <span className="h5">{t("bulk-senders.data-status")}: </span>
                            <span className={statusDetails.className}>
                                {statusDetails.icon} {statusDetails.statusText}
                            </span>
                        </p>
                        <p className="mb-0 text-nowrap">
                            <span className="h5">{t("bulk-senders.data-name-sub")}: </span> {row.info.sub_account_name || t("bulk-senders.data-no-info")}
                        </p>
                        <p className="mb-0 text-nowrap">
                            <span className="h5">{t("bulk-senders.data-first-send")}: </span> {row.status.first_sent || t("bulk-senders.data-no-info")}
                        </p>
                        <p className="mb-0 text-nowrap">
                            <span className="h5">{t("bulk-senders.data-last-send")}: </span> {row.status.last_sent || t("bulk-senders.data-no-info")}
                        </p>
                        <p className="mb-0 text-nowrap">
                            <span className="h5">{t("bulk-senders.data-duration")}: </span> {row.status.duration || t("bulk-senders.data-no-info")}
                        </p>
                    </>
                );
            }
        },
        {
            name: t("bulk-senders.actions"),
            sortable: false,
            center: true,
            cell: (row: any) => {
                return (
                    <div className="btn-group" role="group">
                        <Button className="btn btn-white btn-sm" onClick={() => downloadReport(row)} >
                            <i className="bi-download"></i> {t("bulk-senders.download")}
                        </Button>


                    </div>
                )
            }
        },
    ];

    const downloadReport = async (row) => {
        const promise = async () => {
            const response = await BulkSenderService.downloadReport(row.info.base_id)

            const blob = new Blob([response], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.href = url;
            link.setAttribute("download", `${row.name}.csv`);
            link.style.display = "none";

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            URL.revokeObjectURL(url);
        }

        AlertService.toastLoading(promise(), 'Creando reporte...', 'Se generó correctamente el reporte', 'Ocurrió un error al generar el reporte, intenta nuevamente')
    }

    const getReport = async () => {

        if (filterRangeDates.start && filterRangeDates.end) {
            const start = filterRangeDates.start.format('YYYY-MM-DD')
            const end = filterRangeDates.end.format('YYYY-MM-DD')

            const { data } = await call(() => BulkSenderService.report(account.id, start, end));
            setData(data)
        }
    }

    useEffect(() => {
        getReport()
    }, [filterRangeDates])


    return (
        <>
            <Header />
            <Aside />

            <main id="content" role="main" className="main">

                <div className="content container-fluid">

                    <div className="page-header">
                        <div className="row align-items-center">
                            <div className="col-sm mb-2 mb-sm-0">
                                <nav aria-label="breadcrumb">
                                    <ol className="breadcrumb breadcrumb-no-gutter">
                                        <li className="breadcrumb-item "><span className="breadcrumb-link" >{t("reports.operation-title")}</span></li>
                                        <li className="breadcrumb-item active" aria-current="page">{t("bulk-senders.bulk-title")}</li>
                                    </ol>
                                </nav>
                                <h1 className="page-header-title"><i className="bi-send-plus nav-icon"></i> {t("bulk-senders.bulk-title")}</h1>
                            </div>
                            <div className="col-sm-auto">
                                <button className="btn btn-primary btn-sm" onClick={() => navigate('/campaigns/create')} ><i className="bi-chat-dots-fill me-1"></i>{t("bulk-senders.add-campaign")} </button>
                            </div>
                        </div>
                    </div>

                    <CustomDataTable
                        columns={columns}
                        showDates
                        data={data}
                        hideAccountsFilter
                        filterDay={0}
                        showExport={false}
                        isLoading={isRequesting}
                        onDatesChange={(range) => setFilterRangeDates(range)}
                        filter={(row, term) => {
                            if (!term) return true;

                            return (
                                `${row.name}`.match(new RegExp(`${term}`, 'i'))
                            )
                        }}
                    />

                </div>
                <Footer />
            </main>
        </>
    )
}
export default BulkSendersPage